import { Dog } from '@/types/dog';
import { InbreedingRiskLevel, AncestorPath, CommonAncestor } from '@/types/ai';

/**
 * 近親係数の閾値定義
 */
export const INBREEDING_THRESHOLDS = {
  /** 安全（6.25%未満） */
  SAFE: 0.0625,
  /** 注意（12.5%未満） */
  CAUTION: 0.125,
  /** 警告（25%未満） */
  WARNING: 0.25,
} as const;

/**
 * 近親係数からリスクレベルを判定
 * @param coefficient 近親係数（0-1）
 * @returns リスクレベル
 */
export function getRiskLevel(coefficient: number): InbreedingRiskLevel {
  if (coefficient < INBREEDING_THRESHOLDS.SAFE) {
    return 'safe';
  }
  if (coefficient < INBREEDING_THRESHOLDS.CAUTION) {
    return 'caution';
  }
  if (coefficient < INBREEDING_THRESHOLDS.WARNING) {
    return 'warning';
  }
  return 'danger';
}

/**
 * Wright's Coefficient of Inbreeding（ライトの近親係数）を計算するクラス
 *
 * 近親係数の計算式: F = Σ (1/2)^(n1+n2+1) × (1 + Fa)
 * - n1: 父方から共通祖先までの世代数
 * - n2: 母方から共通祖先までの世代数
 * - Fa: 共通祖先自身の近親係数（簡略化のため0と仮定）
 */
export class InbreedingCalculator {
  /** 計算する最大世代数 */
  private readonly MAX_GENERATIONS = 6;

  /**
   * 仮想的な子犬の近親係数を計算
   * @param fatherId オス犬（父犬）のID
   * @param motherId メス犬（母犬）のID
   * @param ancestorMap 祖先の犬データマップ（ID → Dog）
   * @returns 近親係数（0-1）
   */
  calculateCoefficient(
    fatherId: string,
    motherId: string,
    ancestorMap: Map<string, Dog>
  ): number {
    // 祖先マップが空の場合は0を返す
    if (ancestorMap.size === 0) {
      return 0;
    }

    // 両親の祖先パスを取得
    const fatherPaths = this.getAncestorPaths(fatherId, ancestorMap);
    const motherPaths = this.getAncestorPaths(motherId, ancestorMap);

    // 共通祖先を検出
    const commonAncestors = this.findCommonAncestors(fatherPaths, motherPaths);

    // 各共通祖先の寄与を計算して合計
    let totalCoefficient = 0;
    for (const ancestor of commonAncestors) {
      totalCoefficient += ancestor.contribution;
    }

    // 近親係数は最大1
    return Math.min(totalCoefficient, 1);
  }

  /**
   * 指定した犬から全祖先へのパスを取得
   * @param dogId 犬のID
   * @param ancestorMap 祖先マップ
   * @returns 祖先パスのマップ（祖先ID → パス配列）
   */
  private getAncestorPaths(
    dogId: string,
    ancestorMap: Map<string, Dog>
  ): Map<string, AncestorPath[]> {
    const paths = new Map<string, AncestorPath[]>();
    this.traverseAncestors(dogId, 0, ancestorMap, paths);
    return paths;
  }

  /**
   * 再帰的に祖先を辿ってパスを記録
   * @param dogId 現在の犬ID
   * @param generation 現在の世代数（0から開始）
   * @param ancestorMap 祖先マップ
   * @param paths パスマップ（結果格納用）
   */
  private traverseAncestors(
    dogId: string,
    generation: number,
    ancestorMap: Map<string, Dog>,
    paths: Map<string, AncestorPath[]>
  ): void {
    // 最大世代数を超えたら終了
    if (generation > this.MAX_GENERATIONS) {
      return;
    }

    const dog = ancestorMap.get(dogId);
    if (!dog) {
      return;
    }

    // 現在の犬をパスに追加（generation > 0 の場合のみ = 祖先のみ記録）
    if (generation > 0) {
      const existingPaths = paths.get(dogId) || [];
      existingPaths.push({
        ancestorId: dogId,
        generations: generation,
      });
      paths.set(dogId, existingPaths);
    }

    // 父方を再帰的に辿る
    if (dog.fatherId) {
      this.traverseAncestors(dog.fatherId, generation + 1, ancestorMap, paths);
    }

    // 母方を再帰的に辿る
    if (dog.motherId) {
      this.traverseAncestors(dog.motherId, generation + 1, ancestorMap, paths);
    }
  }

  /**
   * 共通祖先を検出し、近親係数への寄与を計算
   * @param fatherPaths 父方の祖先パス
   * @param motherPaths 母方の祖先パス
   * @returns 共通祖先のリスト
   */
  private findCommonAncestors(
    fatherPaths: Map<string, AncestorPath[]>,
    motherPaths: Map<string, AncestorPath[]>
  ): CommonAncestor[] {
    const commonAncestors: CommonAncestor[] = [];

    // 父方の祖先をループ
    for (const [ancestorId, fPaths] of fatherPaths) {
      // 母方にも同じ祖先がいるか確認
      const mPaths = motherPaths.get(ancestorId);
      if (!mPaths) {
        continue;
      }

      // 全ての経路の組み合わせについて寄与を計算
      for (const fPath of fPaths) {
        for (const mPath of mPaths) {
          // Wright's formula: (1/2)^(n1+n2+1)
          // n1: 父方からの世代数, n2: 母方からの世代数
          const contribution = Math.pow(
            0.5,
            fPath.generations + mPath.generations + 1
          );

          commonAncestors.push({
            ancestorId,
            fatherPathLength: fPath.generations,
            motherPathLength: mPath.generations,
            contribution,
          });
        }
      }
    }

    return commonAncestors;
  }

  /**
   * 近親係数のリスクレベルを取得
   * @param coefficient 近親係数
   * @returns リスクレベル
   */
  getRiskLevel(coefficient: number): InbreedingRiskLevel {
    return getRiskLevel(coefficient);
  }
}

// シングルトンインスタンスをエクスポート
export const inbreedingCalculator = new InbreedingCalculator();
